import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { ExperienceContext } from '../../contexts/ExperienceContext';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import Button from '../UI/Button';
import AgGridWrapper from '../UI/AgGrid';
import ConfirmModal from '../UI/Modal/confirmModal';
import { toast } from 'react-toastify';

const DeleteExperienceWithDependencyModal = ({ selectedExperience, setSelectedExperiences, setSelectedAction, setSelectedExperience }) => {
    const { userData } = useUserContext();
    const { content } = useContentfulContext();
    const { deleteExperienceHandler } = useContext(ExperienceContext);
    const modalText = content.MANAGE_EXPERIENCE_CONTENT?.delete_modal || {};

    const [dependencies, setDependencies] = useState(null);
    const [message, setMessage] = useState('');
    const [showGrid, setShowGrid] = useState(false);
    const [error, setErrors] = useState('');
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const getSelectedExperienceIds = () => {
        if (!selectedExperience) return [];
        if (Array.isArray(selectedExperience)) {
            return selectedExperience.map(exp => exp._id);
        } else {
            return selectedExperience.id;
        }
    };

    // Fetch dependencies on open
    useEffect(() => {
        const fetchDependencies = async () => {
            try {
                setLoading(true);
                const ids = getSelectedExperienceIds();
                const result = await deleteExperienceHandler(ids, true);

                if (result?.data && Object.keys(result.data).length > 0) {
                    setDependencies(result.data);
                    setMessage(result.message);
                }
            } catch (err) {
                setErrors(err?.message || modalText.dependency_fetch_error || "Failed to fetch dependencies");
            } finally {
                setLoading(false);
            }
        };
        fetchDependencies();
    }, [selectedExperience, userData]);

    const handleDeleteProduct = async () => {
        try {
            setActionLoading(true);
            const ids = getSelectedExperienceIds();
            const result = await deleteExperienceHandler(ids);
            if (result) {
                setSelectedAction("");
                toast.success(result?.message);
                setShowConfirm(false);
                if (ids) {
                    setSelectedExperiences([]);
                } else {
                    setSelectedExperience(null);
                }
            }
        } catch (error) {
            setErrors(error?.message || modalText.delete_failed_error || "Delete failed");
        } finally {
            setActionLoading(false);
        }
    };

    // Flatten dependencies for grid preview
    const rowData = useMemo(() => {
        const rows = [];
        if (dependencies?.experience_offer_mappings?.length) {
            dependencies.experience_offer_mappings.forEach(mapping => {
                if (Array.isArray(mapping.records)) {
                    mapping.records.forEach(d => {
                        rows.push({
                            type: 'Experience Mapping',
                            destination: d?.destination,
                            id: d?.id,
                            ...d
                        });
                    });
                }
            });
        }
        return rows;
    }, [dependencies]);

    const colDefs = [
        { headerName: 'Type', field: 'type', flex: 1 },
        { headerName: 'Destination', field: 'destination', flex: 2 },
        { headerName: 'ID', field: 'id', flex: 2 }
    ];

    return (
        <>
            <PrimaryModal loading={actionLoading} title={modalText.review_dependency_title || "Review dependency"} hideModal={() => setSelectedAction("")}>
                <div className="p-6 space-y-6">
                    {loading ? (
                        <p className="text-center text-gray-500">{modalText.checking_dependencies || "Checking dependencies..."}</p>
                    ) : (
                        <>
                            {!showGrid && (
                                <>
                                    {message ? (
                                        <p className="text-base text-black mb-4">
                                            {message} <br />
                                            <span className="font-semibold text-red-500">
                                                {modalText.recommendation_text || "It’s recommended reviewing these dependencies before deleting the Experience."}
                                            </span>
                                        </p>
                                    ) : (
                                        <p className="text-base text-black mb-4">
                                            {modalText.confirm_msg || "Are you sure to delete the experience?"}
                                        </p>
                                    )}
                                    {error && <p className="text-danger text-xs font-medium">{error}</p>}
                                    <div className="mt-6 flex justify-end gap-2.5">
                                        {message ? (
                                            <>
                                                <Button variant="outline" size="sm" onClick={() => setShowGrid(true)}>
                                                    {modalText.review_dependencies_cta || "Review Dependencies"}
                                                </Button>
                                                <Button variant="danger" size="sm" onClick={() => setShowConfirm(true)}>
                                                    {modalText.delete_experience_cta || "Delete Experience"}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                loading={actionLoading}
                                                onClick={() => {
                                                    if (message) {
                                                        setShowConfirm(true);
                                                    } else {
                                                        handleDeleteProduct();
                                                    }
                                                }}
                                            >
                                                {modalText.delete_experience_cta || "Delete Experience"}
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )}

                            {showGrid && (
                                <div>
                                    <p className="mb-2 font-semibold text-xs text-stride-muted uppercase tracking-wider">{modalText.dependencies_preview || "Dependencies Preview"}</p>
                                    <div className="ag-theme-quartz border border-border rounded-xl overflow-hidden" style={{ height: "300px", width: "100%" }}>
                                        <AgGridWrapper
                                            tableId="experience-dependencies-table"
                                            rowData={rowData}
                                            columnDefs={colDefs}
                                        />
                                    </div>
                                    <div className="mt-4 flex justify-end">
                                        <Button variant="outline" size="sm" onClick={() => setShowGrid(false)}>
                                            {modalText.back_cta || "Back"}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </PrimaryModal>
            {showConfirm && (
                <ConfirmModal
                    title={modalText.confirm_delete_title || "Confirm Deletion"}
                    description={modalText.confirm_delete_description || "This change cannot be undone. Are you sure you want to delete this Experience?"}
                    ConfirmText={modalText.confirm_delete_cta || "Delete"}
                    error={error}
                    loading={actionLoading}
                    hideModal={setShowConfirm}
                    handleConfirmation={(confirmed) => {
                        if (confirmed) {
                            handleDeleteProduct();
                        } else {
                            setShowConfirm(false);
                        }
                    }}
                />
            )}
        </>
    );
};

export default DeleteExperienceWithDependencyModal;